<script setup lang="ts">
import { computed, ref } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import {
  mdiTruck,
  mdiWarehouse,
  mdiBriefcaseRemove,
  mdiBriefcaseAccount,
  mdiPackageVariantClosed,
  mdiPackageVariantRemove,
} from '@mdi/js'
import { useSearchStore } from '@/modules/search/stores/search.store'
import { useSelected } from '@/modules/motion/stores/selected.store'
import { toKebabCase } from '@/shared/utils/format'
import type { ClientView } from '@/api/models/types'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import BaseDropdown from '@/shared/components/dropdown/BaseDropdown.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'

interface Props {
  title: string
  views: ClientView[]
  disabled?: boolean
}

interface Emits {
  (e: 'change'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<number | undefined>({ required: true })

const searchStore = useSearchStore()
const selectStore = useSelected()
const isActive = ref(false)

const name = toKebabCase(props.title)

const icons: Record<number, string> = {
  1000: mdiTruck,
  2000: mdiWarehouse,
  3000: mdiPackageVariantClosed,
  3300: mdiBriefcaseAccount,
  4000: mdiPackageVariantRemove,
  4300: mdiBriefcaseRemove,
}

const selectedView = computed(() => props.views.find((v) => v.viewId === model.value))

const viewName = computed(() => selectedView.value?.viewName ?? '')

const currentIcon = computed(() =>
  selectedView.value ? (icons[selectedView.value.viewId] ?? '') : '',
)

const updateModel = (view: ClientView): void => {
  selectStore.selection.clear()
  selectStore.lastSelectedId = null
  searchStore.isSearchActive = false
  searchStore.relatedId = undefined
  model.value = view.viewId
  isActive.value = false

  emits('change')
}

const onToggle = (): void => {
  isActive.value = !isActive.value
}
</script>

<template>
  <div class="relative" v-on-click-outside="() => (isActive = false)">
    <div class="flex items-center border-r border-y border-gray-300 dark:border-gray-400">
      <div class="absolute left-0 m-2">
        <SvgIcon class="fill-white dark:fill-white h-4 w-4" :path="currentIcon" />
      </div>
      <input
        type="text"
        autocomplete="off"
        spellcheck="false"
        class="peer block text-sm truncate w-full pe-6 border-0 bg-transparent p-1 outline-none transition-all duration-200 ease-linear motion-reduce:transition-none enabled:cursor-pointer text-white disabled:bg-white disabled:text-gray-400"
        :class="currentIcon && 'ps-8'"
        placeholder=""
        :id="name"
        :name="name"
        :title="title"
        :readonly="true"
        :value="viewName"
        @click="onToggle()"
        :disabled="disabled"
      />
      <CaretIcon v-if="views.length > 1" :active="isActive" class="absolute right-0 m-2" />
    </div>
    <BaseDropdown v-if="views.length > 1" :open="isActive">
      <ul class="cursor-pointer dark:bg-black">
        <li
          class="group text-sm truncate dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white p-2"
          v-for="(view, index) in views"
          :key="index"
          @click="updateModel(view)"
        >
          <div class="flex items-center">
            <div class="me-1.5">
              <SvgIcon
                class="fill-gray-700 dark:fill-gray-400 dark:group-hover:fill-white h-4 w-4"
                :path="icons[view.viewId]"
              />
            </div>
            <div class="truncate text-sm">{{ view.viewName }}</div>
          </div>
        </li>
      </ul>
    </BaseDropdown>
  </div>
</template>
